import { loadModules } from 'esri-loader';
import { bindActionCreators } from 'redux';
import {
  getOrderByFromSort,
  joinWhereClause,
  getWhereFromFilters
} from './queryUtils';
import originFields from '../defaults/PointOfOriginFields.json';
import { resourceOperations } from '../redux/resource';
import { configOperations } from '../redux/config';

export default class LayerManager {
  constructor(view, layers, tables, config) {
    return new Promise((resolve, reject) => {
      loadModules(['esri/layers/FeatureLayer']).then(([FeatureLayer]) => {
        this.view = view;
        this.map = view.map;
        this.config = config;
        this.layers = layers;
        this.tables = tables;

        // References to map layers/tables
        this.incidents = this.layers[config.layers.INCIDENTS];
        this.incidentsHeatmap = this.layers[config.layers.INCIDENTS_HEATMAP];
        this.incidentHistory = this.layers[config.layers.INCIDENT_HISTORY];
        this.resources = this.layers[config.layers.RESOURCES];

        // Clones of map layers/tables
        this.incidentRelationships = this.tables[
            config.tables.INCIDENT_RELATIONSHIPS
            ];
        this.incidentResources = this.tables[config.tables.INCIDENT_RESOURCES];
        this.resourceExperience = this.tables[
            config.tables.RESOURCE_EXPERIENCE
            ];
        this.resourceCapabilities = this.tables[
            config.tables.RESOURCE_CAPABILITIES
            ];
        this.resourceCapabilityRequests = this.tables[
            config.tables.RESOURCE_CAPABILITY_REQUESTS
            ];
        this.resourceCapabilityTypes = this.tables[
            config.tables.RESOURCE_CAPABILITY_TYPES
            ];
        this.resourceRelationship = this.tables[
            config.tables.RESOURCE_RELATIONSHIP
            ];

        this.initEventHandlers().then(resolve(this), reject);
      });
    });
  }

  initEventHandlers() {
    return new Promise((resolve, reject) => {
      loadModules(['dojo/topic']).then(([topic]) => {
        if (window.subscriptions && window.subscriptions.length) {
          for (const key in window.subscriptions) {
            if (window.subscriptions.hasOwnProperty(key)) {
              window.subscriptions[key].remove();
              delete window.subscriptions[key];
            }
          }
        }

        window.subscriptions = [];

        window.subscriptions.push(
            topic.subscribe(
                'LayerManager/fetch-incident-by-id',
                this.fetchIncidentById.bind(this)
            )
        );
        window.subscriptions.push(
            topic.subscribe(
                'LayerManager/fetch-resource-by-id',
                this.fetchResourceById.bind(this)
            )
        );

        window.subscriptions.push(
            topic.subscribe(
                'LayerManager/fetch-resource-info-by-id',
                this.fetchResourceInfoById.bind(this)
            )
        );
        window.subscriptions.push(
            topic.subscribe(
                'LayerManager/fetch-incidents',
                this.fetchIncidents.bind(this)
            )
        );
        window.subscriptions.push(
            topic.subscribe(
                'LayerManager/fetch-many-incidents',
                this.fetchManyIncidents.bind(this)
            )
        );
        window.subscriptions.push(
            topic.subscribe(
                'LayerManager/fetch-incident-count',
                this.fetchIncidentCount.bind(this)
            )
        );
        window.subscriptions.push(
            topic.subscribe(
                'LayerManager/fetch-incident-history',
                this.fetchIncidentHistory.bind(this)
            )
        );
        window.subscriptions.push(
            topic.subscribe(
                'LayerManager/fetch-incident-209-resources',
                this.fetchResources209.bind(this)
            )
        );
        window.subscriptions.push(
            topic.subscribe(
                'LayerManager/fetch-incident-209-resources',
                this.fetchResourceRequests.bind(this)
            )
        );
        window.subscriptions.push(
            topic.subscribe(
                'LayerManager/fetch-resources-by-incident-id',
                this.fetchResourcesByIncidentID.bind(this)
            )
        );
        window.subscriptions.push(
            topic.subscribe(
                'LayerManager/fetch-requests-by-incident-id',
                this.fetchRequestsByIncidentID.bind(this)
            )
        );
        window.subscriptions.push(
            topic.subscribe(
                'LayerManager/fetch-operational-statuses-by-incident-id',
                this.fetchOperationalStatusesByIncidentID.bind(this)
            )
        );
        window.subscriptions.push(
            topic.subscribe(
                'LayerManager/fetch-experiences-by-incident-id',
                this.fetchExperiencesByIncidentID.bind(this)
            )
        );
        window.subscriptions.push(
            topic.subscribe(
                'LayerManager/fetch-dispatch-ids',
                this.fetchDispatchIDs.bind(this)
            )
        );
        window.subscriptions.push(
            topic.subscribe(
                'LayerManager/fetch-resources-by-current-dispatch',
                this.fetchResourcesByCurrentDispatch.bind(this)
            )
        );
        window.subscriptions.push(
            topic.subscribe(
                'LayerManager/fetch-resource-by-id',
                this.fetchResourceById.bind(this)
            )
        );
        window.subscriptions.push(
            topic.subscribe(
                'LayerManager/fetch-capabilities-by-requests',
                this.fetchCapabilitiesbyRequests.bind(this)
            )
        );

        window.subscriptions.push(
            topic.subscribe(
                'LayerManager/fetch-ctids',
                this.fetchCTIDs.bind(this)
            )
        );

        window.subscriptions.push(
            topic.subscribe(
                'LayerManager/fetch-resource-relationships',
                this.fetchResourceRelationships.bind(this)
            )
        );
        window.subscriptions.push(
            topic.subscribe(
                'LayerManager/fetch-resource-relationships-by-rid',
                this.fetchResourceRelationships.bind(this)
            )
        );
        window.subscriptions.push(
            topic.subscribe(
                'LayerManager/fetch-resources',
                this.fetchResources.bind(this)
            )
        );
        window.subscriptions.push(
            topic.subscribe(
                'LayerManager/fetch-many-resources',
                this.fetchManyResources.bind(this)
            )
        );

        window.subscriptions.push(
            topic.subscribe(
                'LayerManager/zoom-to-geometry',
                this.zoomToGeometry.bind(this)
            )
        );
        /* / window.subscriptions.push(
          topic.subscribe(
            'LayerManager/zoom-to-many-geometry',
            this.zoomToManyGeometry.bind(this)
          )
        ); */
        resolve();
      });
    });
  }

  // //////////////////
  // PUBLIC METHODS //
  // //////////////////

  //
  // INCIDENTS
  //

  fetchIncidentById(id, options, done, failed) {
    const requests = [];
    if (options && options.includeRelationships) {
      const getRelationships = this.fetchIncidentRelationships(id);
      requests.push(getRelationships);
      // console.log(getRelationships);
    }

    const getIncident = this._queryFeaturesById(
        this.incidents,
        id,
        originFields.idFields.incidents
    );
    requests.unshift(getIncident);

    Promise.all(requests).then(
        results => {
          if (!results[0].features.length) {
            // No feature was returned
            done(null);
            return;
          }

          const incident = results[0].features[0];

          // Relationships weren't requested
          if (results.length === 1) {
            done(incident);
            return;
          }

          // Relationships were requested, but there are none
          if (results[1].features.length === 0) {
            incident.relationships = [];
            done(incident);
            return;
          }

          // Relationships were requested and one or more exist
          const relationshipRecords = results[1].features;
          const parentId = originFields.incidentRelationshipsIdFields.parent;
          const childId = originFields.incidentRelationshipsIdFields.child;

          // ID of related feature is the one that doesn't match id in-hand
          relationshipRecords.forEach(f => {
            if (f.attributes[childId] === id) {
              f.id = f.attributes[parentId];
              f.isChild = false;
            } else {
              f.id = f.attributes[childId];
              f.isChild = true;
            }
            //  console.log(f, f.attributes[childId], f.isChild, f.id);
          });

          const ids = relationshipRecords.map(f => f.id);
          const url = `${this.incidents.url}/${this.incidents.layerId}`;
          const options = {
            where: `${originFields.idFields.incidents} IN ('${ids.join(
                "','"
            )}') AND (IsQuarantined IN (1,0))`,
            returnGeometry: true
          };
          // console.log(ids);
          this._executeQueryTask(url, options).then(
              relatedIncidents => {
                const relationships = relatedIncidents.features.map(f => {
                  const relAttr = relationshipRecords.filter(
                      relationshipRecord =>
                          relationshipRecord.id ===
                          f.attributes[originFields.idFields.incidents]
                  )[0];
                  f.relationshipInfo = relAttr;
                  return f;
                });

                incident.relationships = relationships;
                done(incident);
              },
              error => {
                failed(error);
              }
          );
        },
        error => {
          failed(error);
        }
    );
  }

  fetchIncidentRelationships(id) {
    return new Promise((resolve, reject) => {
      const url = this.incidentRelationships.url;
      const parentId = originFields.incidentRelationshipsIdFields.parent;
      const childId = originFields.incidentRelationshipsIdFields.child;
      const options = {
        where: `(${parentId} = '${id}' OR ${childId} = '${id}') AND IsActive = 1`
      };
      this._executeQueryTask(url, options).then(resolve, reject);
    });
  }

  // This is the primary method used for querying the incidents table,
  // and for updating map layers' definition expressions.
  fetchIncidents(options, done, failed) {
    this._queryForFeaturesAndCount(this.incidents, options).then(done, failed);

    // Update the definition expressions of the incidents and heatmap layers
    this._updateFeatureLayer(this.incidents, options);

    this._updateFeatureLayer(this.incidentsHeatmap, options);
  }

  // Query the incidents table in chunked requests
  // in order to get all available incident features (beyond maxRecordCount).
  // Currently used for CSV downloading.
  fetchManyIncidents(options, done, failed, resultFeatures = []) {
    this._queryForManyFeaturesWithRecursion(this.incidents, options).then(
        done,
        failed
    );
  }

  // Fetch the count of all features in the incidents table
  fetchIncidentCount(done, failed) {
    this.incidents.queryFeatureCount().then(done, failed);
  }

  fetchIncidentHistory(options, done, failed) {
    this._queryForFeaturesAndCount(this.incidentHistory, options).then(
        done,
        failed
    );
  }

  fetchResources209(options, done, failed) {
    this._executeQueryTask(this.incidentResources.url, options).then(
        done,
        failed
    );
  }

  /**
   * @func fetchResourcesByIncidentID
   * @desc Execute the query to get the resources by incident ID.
   *
   * @param {Object} options Options for the query.
   * @param {string} options.where The where clause.
   * @param {Function} done The callback function.
   * @param {Function} failed The errback function.
   */
  fetchResourcesByIncidentID(incidentID, done, failed) {
    const fetchRIDsByCIDs = this.fetchRIDsByCIDs.bind(this);
    const fetchResourcesByRIDs = this.fetchResourcesByRIDs.bind(this);

    if (this.fetchCIDsByID(incidentID) !== []) {
      this.fetchCIDsByID(incidentID)
          .then(fetchRIDsByCIDs, done, failed)
          .then(fetchResourcesByRIDs, done, failed)
          .then(done, failed);
    }
  }

  fetchRequestsByIncidentID(irwinID, done, failed) {
    const CAPABILITY_REQUEST_URL = this.resourceCapabilityRequests.url;
    const OPTIONS = {
      where: `(IrwinID = '${irwinID}')`,
      outFields: `*`
    };
    this._executeQueryTask(CAPABILITY_REQUEST_URL, OPTIONS).then(done, failed);
  }

  fetchOperationalStatusesByIncidentID(irwinID, done, failed) {
    const CAPABILITY_REQUEST_URL = this.resourceCapabilityRequests.url;
    const OPTIONS = {
      where: `(IrwinID = '${irwinID}')`,
      outFields: `*`
    };

    return this._executeQueryTask(CAPABILITY_REQUEST_URL, OPTIONS);
  }

  fetchExperiencesByIncidentID(incidentID, done, failed) {
    const EXPERIENCE_URL = this.resourceExperience.url;
    const OPTIONS = {
      where: `(IrwinID = '${incidentID}')`,
      outFields: `*`
    };

    return this._executeQueryTask(EXPERIENCE_URL, OPTIONS).then(done, failed);
  }

  fetchCapabilitiesbyRequests(incidentID, done, failed) {
    const fetchCIDsByCIDs = this.fetchCIDsByCIDs.bind(this);
    this.fetchCIDsByID(incidentID);

    if ([] !== this.fetchCIDsByID(incidentID)) {
      this.fetchCIDsByID(incidentID)
          .then(fetchCIDsByCIDs, failed)
          .then(done, failed);
    }
  }

  /**
   * @func fetchCIDsByID
   * @desc The
   *
   * @param {string} irwinID
   */
  fetchCIDsByID(irwinID) {
    const CAPABILITY_REQUEST_URL = this.resourceCapabilityRequests.url;
    const OPTIONS = {
      where: `(IrwinID = '${irwinID}')`,
      outFields: `IrwinCID`
    };

    return this._executeQueryTask(CAPABILITY_REQUEST_URL, OPTIONS);
  }

  fetchCTIDsByID(irwinID) {
    const CAPABILITY_REQUEST_URL = this.resourceCapabilityRequests.url;
    const OPTIONS = {
      where: `(IrwinID = '${irwinID}')`,
      outFields: `IrwinCTID`
    };

    return this._executeQueryTask(CAPABILITY_REQUEST_URL, OPTIONS);
  }

  /**
   *
   * @param {*} param0
   * @param {}
   *
   * @todo Typecheck the parameter.
   */
  fetchRIDsByCIDs({features = []}) {
    let IRWIN_CID_SET = features.map(
        ({attributes: {IrwinCID}}) => IrwinCID
    );
    IRWIN_CID_SET = IRWIN_CID_SET.filter(item => item !== null);
    const uniqueSet = IRWIN_CID_SET;
    const CAPABILITY_URL = this.resourceCapabilities.url;
    const OPTIONS = {
      outFields: 'IrwinRID'
    };
    // let whereClause = '';
    //
    // if (uniqueSet.length > 0) {
    //   // whereClause = `IrwinCID = '${uniqueSet.join(`' OR IrwinCID = '`)}'`;
    //   // OPTIONS.where = whereClause;
    // } else {
    //   //   const queryPromises = [];
    //   //   for (let i = 0; i < uniqueSet.length; i += 100) {
    //   //     const slicedArray = uniqueSet.slice(i, i + 100);
    //   //     whereClause = `IrwinCID = '${slicedArray.join(`' OR IrwinCID = '`)}'`;
    //   //     OPTIONS.where = whereClause;
    //   //     queryPromises.push(
    //   //         this._executeQueryTask(CAPABILITY_URL, OPTIONS));
    //   //   }
    //   //     return Promise.all(queryPromises);
    //   // }
    //   whereClause = `CreatedOnDateTime < 1514764800000`;
    //   OPTIONS.where = whereClause;
    // }
    return this._queryForRequestFeaturesAndCount(
        CAPABILITY_URL,
        'IrwinCID',
        OPTIONS,
        uniqueSet
    );
  }

  fetchCIDsByCIDs({features = []}) {
    let IRWIN_CID_SET = features.map(
        ({attributes: {IrwinCID}}) => IrwinCID
    );
    IRWIN_CID_SET = IRWIN_CID_SET.filter(item => item !== null);
    const uniqueSet = IRWIN_CID_SET;
    const CAPABILITY_URL = this.resourceCapabilities.url;
    const OPTIONS = {
      outFields: '*'
    };
    const whereClause = '';

    // if (uniqueSet.length > 0) {
    //     //whereClause = `IrwinCID = '${uniqueSet.join(`' OR IrwinCID = '`)}'`;
    // } else {
    //   whereClause = `CreatedOnDateTime < 1514764800000`;
    // }
    // OPTIONS.where = whereClause;
    return this._queryForRequestFeaturesAndCount(
        CAPABILITY_URL,
        'IrwinCID',
        OPTIONS,
        uniqueSet
    );
  }

  fetchCTIDsByCRIDs({features = []}) {
    let IRWIN_CRID_SET = features.map(
        ({attributes: {IrwinCTID}}) => IrwinCTID
    );
    IRWIN_CRID_SET = IRWIN_CRID_SET.filter(item => item !== null);
    const uniqueSet = [...new Set(IRWIN_CRID_SET)];
    const CAPABILITY_TYPE_URL = this.resourceCapabilityTypes;
    const OPTIONS = {
      outFields: '*'
    };
    const whereClause = '';
    // if (IRWIN_CRID_SET.length > 0) {
    //   whereClause = `IrwinCTID = '${IRWIN_CRID_SET.join(`' OR IrwinCTID = '`)}'`;
    // }
    //
    // OPTIONS.where = whereClause;
    // // console.log(IRWIN_CRID_SET);
    return this._queryForRequestFeaturesAndCount(
        CAPABILITY_TYPE_URL,
        'IRWINCTID',
        OPTIONS,
        uniqueSet
    );
  }

  fetchCTIDs(incidentID, done, failed) {
    const fetchCTIDsByCRIDs = this.fetchCTIDsByCRIDs.bind(this);
    this.fetchCTIDsByID(incidentID);

    if ([] !== this.fetchCTIDsByID(incidentID)) {
      this.fetchCTIDsByID(incidentID)
          .then(fetchCTIDsByCRIDs, failed)
          .then(done, failed);
    }
  }

  /**
   *
   * @param {*} param0
   *
   * @todo Typecheck the parameter features.
   */
  fetchResourcesByRIDs({features = []}) {
    let IRWIN_RID_SET = features.map(
        ({attributes: {IrwinRID}}) => IrwinRID
    );
    const RESOURCE_LAYER = this.resources.url + '/0/';
    const QUERY = {};
    let whereClause = '';
    IRWIN_RID_SET = IRWIN_RID_SET.filter(item => item !== null);
    const uniqueSet = [...new Set(IRWIN_RID_SET)];
    if (uniqueSet.length > 0) {
      whereClause = `IrwinRID = '${uniqueSet.join(`' OR IrwinRID = '`)}'`;
    } else {
      //whereClause = `1=1`;
      QUERY.num = 1;
      QUERY.start = 0;
      QUERY.returnGeometry = false;
    }
    const OPTIONS = {
      where: whereClause,
      outFields: '*'
    };

    QUERY.where = whereClause;
    QUERY.outFields = ['*'];
    return this._queryForRequestFeaturesAndCount(RESOURCE_LAYER, 'IrwinRID', OPTIONS, uniqueSet);
  }

  fetchDispatchIDs(done, failed) {
    const RESOURCE_LAYER = this.resources;
    const QUERY = RESOURCE_LAYER.createQuery();
    QUERY.where = '1=1';
    QUERY.outFields = ['CurrentDispatchUnit'];
    QUERY.returnDistinctValues = true;
    QUERY.returnGeometry = false;
    return RESOURCE_LAYER.queryFeatures(QUERY).then(done, failed);
  }

  fetchRelationshipsByRID(ParentIrwinRID) {
    const RESOURCE_RELATIONSHIP_LAYER = this.resourceRelationship.url;
    const OPTIONS = {
      outFields: 'ChildIrwinCID, ParentIrwinRID'
    };
    const whereClause = `(ParentIrwinRID = '${ParentIrwinRID}')`;

    OPTIONS.where = whereClause;

    return this._executeQueryTask(RESOURCE_RELATIONSHIP_LAYER, OPTIONS);
  }

  fetchRelationshipsByRIDs({features = []}) {
    const RESOURCE_RELATIONSHIP_LAYER = this.resourceRelationship;
    let IRWIN_RID_SET = features.map(
        ({attributes: {IrwinRID}}) => IrwinRID
    );
    // console.log(IRWIN_RID_SET);
    IRWIN_RID_SET = IRWIN_RID_SET.filter(item => item !== null);
    const OPTIONS = {
      outFields: 'ChildIrwinCID, ParentIrwinRID'
    };
    let whereClause = '';
    const uniqueSet = IRWIN_RID_SET;
    if (uniqueSet.length > 0) {
      whereClause = `ParentIrwinRID = '${uniqueSet.join(`' OR ParentIrwinRID
     ='`)}'`;
    }

    OPTIONS.where = whereClause;

    return this._executeQueryTask(RESOURCE_RELATIONSHIP_LAYER, OPTIONS);
  }

  fetchResourceRelationships(incidentID, done, failed) {
    const fetchRIDsByCIDs = this.fetchRIDsByCIDs.bind(this);
    const fetchRelationshipsByRIDs = this.fetchRelationshipsByRIDs.bind(this);
    this.fetchCIDsByID(incidentID);

    if ([] !== this.fetchCIDsByID(incidentID)) {
      this.fetchCIDsByID(incidentID)
          .then(fetchRIDsByCIDs, failed)
          .then(fetchRelationshipsByRIDs, failed)
          .then(done, failed);
    }
  }

  fetchSingleResourceRelationships(id, done, failed) {
    const url = this.resourceRelationship;
    const parentId = originFields.resourceRelationshipsIdFields.parent;
    const childId = originFields.resourceRelationshipsIdFields.child;
    const options = {
      where: `(${parentId} = '${id}') `
    };
    return this._executeQueryTask(url, options).then(done, failed);
  }

  fetchSingleResourceCapability(id, done, failed) {
    const url = this.resourceCapabilities;
    const options = {
      outFields: 'IrwinCID, IrwinCTID',
      where: `(IrwinRID = '${id}') `
    };
    return this._executeQueryTask(url, options).then(done, failed);
  }

  fetchResourcesByCurrentDispatch(currentdispatch, done, failed) {
    const RESOURCE_LAYER = this.resources;
    let highlight;
    const QUERY = RESOURCE_LAYER.createQuery();
    QUERY.where = `CurrentDispatchUnit = '${currentdispatch}'`;
    QUERY.outFields = ['*'];
    return RESOURCE_LAYER.queryFeatures(QUERY).then(done, failed);
  }

  //
  // RESOURCES
  //

  // Fetch a single resource using its IRWIN ID
  fetchResourceById(id, done, failed) {
    this._queryFeaturesById(
        this.resources,
        id,
        originFields.idFields.resources
    ).then(done, failed);
  }

  fetchResourceInfoById(id, options, done, failed) {
    const request = [];
    if (options && options.includeRelationships) {
      const getRelationships = this.fetchSingleResourceRelationships(id);
      request.push(getRelationships);
      const getChildRelationships = this.fetchSingleResourceCapability(id);
      request.push(getChildRelationships);
    }

    const getResource = this._queryFeaturesById(
        this.resources,
        id,
        originFields.idFields.resources
    );
    request.unshift(getResource);
    // console.log(request);
    Promise.all(request).then(
        results => {
          if (!results[0].features.length) {
            // No feature was returned
            done(null);
            return;
          }

          const resource = results[0].features[0];
          // console.log(results[1]);

          // Relationships weren't requested
          if (results.length === 1) {
            done(resource);
            return;
          }

          // Relationships were requested, but there are none
          if (results[1].features.length === 0) {
            resource.relationships = [];
            done(resource);
            return;
          }
          //  console.log(results);
          // Relationships were requested and one or more exist
          const relationshipRecords = results[1].features;
          const relationshipChildRecords = results[2].features;
          const parentId = originFields.resourceRelationshipsIdFields.parent;
          const childId = originFields.resourceRelationshipsIdFields.child;

          // ID of related feature is the one that doesn't match id in-hand
          relationshipRecords.forEach(f => {
            if (f.attributes[childId] === id) {
              f.id = f.attributes[childId];
              f.isChild = true;
              f.IrwinCID = id;
            } else {
              f.id = f.attributes[parentId];
              f.isChild = false;
              f.IrwinCID = f.attributes[childId];
            }
            // console.log(f, f.attributes[childId], f.isChild, f.id);
          });

          // ID of related feature is the one that doesn't match id in-hand
          relationshipChildRecords.forEach(f => {
            if (f.attributes[childId] === id) {
              f.id = f.attributes[childId];
              f.isChild = true;
              f.IrwinCID = id;
            } else {
              f.id = f.attributes[parentId];
              f.isChild = false;
              f.IrwinCID = f.attributes[childId];
            }
          });

          const ids = relationshipRecords.map(f => f.id);
          const url = `${this.resources.url}/${this.resources.layerId}`;
          const options = {
            where: `${originFields.idFields.resources} IN ('${ids.join("','")}')`,
            returnGeometry: true
          };

          const cids = relationshipRecords.map(f => f.IrwinCID);
          const cidurl = `${this.resourceCapabilities.url}`;
          const cidoptions = {
            outFields: 'IrwinRID, IrwinCTID',
            where: `IrwinCID = '${cids.join("' OR IrwinCID = '")}'`,
            returnGeometry: true
          };

          // console.log(ids);
          this._executeQueryTask(url, options)
              .then(
                  relatedResources => {
                    const relationships = relatedResources.features.map(f => {
                      const relAttr = relationshipRecords.filter(
                          relationshipRecord =>
                              relationshipRecord.id ===
                              f.attributes[originFields.idFields.resources]
                      )[0];
                      f.relationshipInfo = relAttr;
                      return f;
                    });

                    resource.relationships = relationships;
                    done(resource);
                  },
                  error => {
                    failed(error);
                  }
              )
              .then(
                  this._executeQueryTask(cidurl, cidoptions).then(
                      relatedResources => {
                        const relationshipscid = relatedResources.features.map(f => {
                          const irwinRids = f.attributes.IrwinRID;
                          const ridOptions = {
                            outFields: 'OperationalName',
                            where: `IrwinRID = '${irwinRids}'`,
                            returnGeometry: true
                          };
                          const relAttr = relationshipRecords.filter(
                              relationshipRecord =>
                                  relationshipRecord.id ===
                                  f.attributes[originFields.idFields.resources]
                          )[0];

                          this._executeQueryTask(url, ridOptions).then(
                              relationshipRecord => {
                                const names = relationshipRecord.features.map(a => {
                                  const name = a.attributes.OperationalName;
                                  f.childNames = name;
                                });
                              }
                          );

                          return f;
                        });

                        resource.childRelationships = relationshipscid;

                        done(resource);
                      },
                      error => {
                        failed(error);
                      }
                  )
              );
        },
        error => {
          failed(error);
        }
    );
  }

  // This is the primary method used for querying the resources table
  fetchResources(options, done, failed) {
    this._queryForFeaturesAndCount(this.resources, options).then(done, failed);
  }

  // This is the primary method used for querying the resources table
  fetchResourceRequests(options, done, failed) {
    this._executeQueryTask(this.resourceCapabilityRequests, options).then(
        done,
        failed
    );
  }

  // Query the incidents table in chunked requests
  // in order to get all available incident features (beyond maxRecordCount).
  // Currently used for CSV downloading.
  fetchManyResources(options, done, failed, resultFeatures = []) {
    this._queryForManyFeaturesWithRecursion(this.resources, options).then(
        done,
        failed
    );
  }

  zoomToGeometry(geometry, done, failed) {
    this.incidents.when(e => {
      this.view.goTo({
        target: [geometry.x, geometry.y],
        zoom: 12
      });
    });
    this.resources.when(e => {
      this.view.goTo({
        target: [geometry.x, geometry.y],
        zoom: 12
      });
    });
  }

  /* / zoomToManyGeometry(results, done, failed) {
    let i;
    let gra;

    if (results.features[0]) {
      for (const feature of results.features) {
        if (feature.geometry !== null) {
          // for xmax and xmin etc.
          const xmin = feature.geometry.x;
          const xmax = feature.geometry.x;
          const ymax = feature.geometry.y;
          const ymin = feature.geometry.y;

          let newExtent = this.extent(
              xmin,
              ymin,
              xmax,
              ymax,
              results.feature.geometry.spatialReference
          );

          for (i = 1; i < results.features.length; i++) {
            gra = results.features[i];
            const xmini = results.features[i].geometry.x;
            const xmaxi = results.features[i].geometry.x;
            const ymaxi = results.features[i].geometry.y;
            const ymini = results.features[i].geometry.y;

            let thisExtent = this.extent(
                xmini,
                ymini,
                xmaxi,
                ymaxi,
                results.features[0].geometry.spatialReference
            );
            // making a union of extent or previous feature and current feature.
            newExtent = newExtent.union(thisExtent);
            this.view.graphics.add(gra);
          }

          this.map.setExtent(newExtent);
        }
      }
    }
  }

   */
  // ///////////////////
  // PRIVATE METHODS //
  // ///////////////////

  // Add an incident's geometry (POOLat / POOLong) to its attributes
  _addGeometryToAttributes(featureSet) {
    featureSet.features.forEach(feature => {
      feature.attributes.POOLatitude = feature.geometry && feature.geometry.y;
      feature.attributes.POOLongitude = feature.geometry && feature.geometry.x;
    });

    return featureSet;

    // Getting this non-mutating method to work in the future would be nice...
    /* const featureSetObj = featureSet.toJSON();

    return {
      ...featureSetObj,
      features: featureSetObj.features.map(feature => ({
          ...feature,
          attributes: {
            ...feature.attributes,
            POOLatitude: feature.geometry.y,
            POOLongitude: feature.geometry.x
          },
        })
      )
    } */
  }

  // This is a helper method to simply fetch a feature by ID
  _queryFeaturesById(layer, id, idField) {
    return new Promise((resolve, reject) => {
      loadModules(['esri/tasks/support/Query']).then(([Query]) => {
        const query = new Query();

        query.sqlFormat = 'standard';

        query.where = `${idField} = '${id}'`;

        // Workaround for Incident API SOI, which filters out
        // quarantined incidents unless explicitly requested
        if (layer.title === this.config.layers.INCIDENTS) {
          query.where = `${query.where} AND (IsQuarantined IN (1,0))`;
        }

        query.outFields = ['*'];
        query.returnGeometry = true;
        layer.queryFeatures(query).then(results => {
          results = this._addGeometryToAttributes(results);
          resolve(results);
        }, reject);
      });
    });
  }

  // This is a generic function to recursively query for features by paging
  // through results and appending them to a tracking array while either:
  //  a. exceededTransferLimit is reported as true by the feature service
  //  b. there are fewer features in the tracking array than the configurable clientMaxRecordCount
  _queryForManyFeaturesWithRecursion(layer, options, resultFeatures = []) {
    // set a default clientMaxRecordCount in case
    // it was not passed in the options arg or provided in a config file
    options.clientMaxRecordCount = options.clientMaxRecordCount || 10000;

    if (resultFeatures.length >= options.clientMaxRecordCount) {
      // exit recursion and provide the resultFeatures array
      return Promise.resolve(resultFeatures);
    }
    // continue querying and appending features to the resultFeatures array

    // establish the concurrent, paged queries
    const concurrentQueriesCount = options.concurrentQueriesCount || 1;
    const queryPromises = [];
    for (let index = 0; index < concurrentQueriesCount; index++) {
      queryPromises.push(
          this._queryForFeatures(layer, {
            ...options,
            page: options.page + index
          })
      );
    }

    // run the queries
    return Promise.all(queryPromises).then(responses => {
      // append features from each query result to the resultFeatures array
      responses.forEach(response => {
        resultFeatures = resultFeatures.concat(response.featureSet.features);
      });

      // if any of the queries have informed us that
      // we have not exceeded the transfer limit,
      // then exit recursion and provide the resultFeatures array
      if (
          responses.some(response => !response.featureSet.exceededTransferLimit)
      ) {
        return Promise.resolve(resultFeatures);
      }

      // set the next page value if querying needs to continue
      // e.g. if there were 5 pages of queries,
      // then add 5 to the next starting page value
      options.page += responses.length;

      // continue recursion to decide if additional querying is needed
      return this._queryForManyFeaturesWithRecursion(
          layer,
          options,
          resultFeatures
      );
    });
  }

  // This is a generic function to:
  //  1. compose and execute a Query operation against a JSAPI FeatureLayer
  _queryForFeatures(layer, options) {
    return new Promise((resolve, reject) => {
      loadModules(['esri/tasks/support/Query']).then(([Query]) => {
        const query = new Query();

        query.sqlFormat = 'standard';

        const {baseQuery = null, sort, filters = []} = options;

        // TODO: determine default outfields
        query.outFields = ['*'];

        // Determined by configuration property `dataGridOptions.defaultPageSize`
        query.num = options.pageSize;

        // Determined by `pageSize` multiplied by the current page number
        query.start = options.page * options.pageSize || null;

        // Determined by react-table's `sorted` property
        query.orderByFields = getOrderByFromSort(sort);

        // Geometry, based on the map extent
        query.geometry = options.extent ? options.extent : null;

        // Return geometry
        query.returnGeometry = true;

        // Determined by react-table's `filtered` property
        const likes = getWhereFromFilters(filters);
        const where = joinWhereClause([baseQuery, likes]);

        query.where = where;

        const featureRequest = layer.queryFeatures(query);

        featureRequest.then(featureSet => {
          featureSet = this._addGeometryToAttributes(featureSet);
          resolve({featureSet});
        }, reject);
      });
    });
  }

  // This is a generic function to:
  //  1. compose and execute a Query operation against a JSAPI FeatureLayer
  //  2. get a count of features matching the query on that FeatureLayer (for grid paging)
  _queryForFeaturesAndCount(layer, options) {
    return new Promise((resolve, reject) => {
      loadModules(['esri/tasks/support/Query']).then(([Query]) => {
        const query = new Query();
        const countQuery = new Query();

        query.sqlFormat = 'standard';
        countQuery.sqlFormat = 'standard';

        const {baseQuery = null, sort, filters = []} = options;

        // TODO: determine default outfields
        query.outFields = ['*'];

        // Determined by configuration property `dataGridOptions.defaultPageSize`
        query.num = options.pageSize;

        // Determined by `pageSize` multiplied by the current page number
        query.start = options.page * options.pageSize || null;

        // Determined by react-table's `sorted` property
        query.orderByFields = getOrderByFromSort(sort);

        // Geometry, based on the map extent
        query.geometry = options.extent ? options.extent : null;
        countQuery.geometry = options.extent ? options.extent : null;

        // Return geometry
        query.returnGeometry = true;

        // Determined by react-table's `filtered` property
        const likes = getWhereFromFilters(filters);
        const where = joinWhereClause([baseQuery, likes]);

        query.where = where;
        countQuery.where = where;

        // We need to know the total number of features that match our query
        // so we can support pagination in the UI, this requires 2 queries
        const featureRequest = layer.queryFeatures(query);
        const countRequest = layer.queryFeatureCount(countQuery);

        Promise.all([featureRequest, countRequest]).then(
            ([featureSet, count]) => {
              featureSet = this._addGeometryToAttributes(featureSet);
              resolve({featureSet, count});
            },
            reject
        );
      });
    });
  }

  _updateFeatureLayer(layer, options) {
    // TODO: is it necessary to return a Promise that doesn't resolve anything?
    //
    //  for example:
    //  this._updateFeatureLayer(this.incidents, options).then(done, failed);
    //  vs
    //  this._updateFeatureLayer(this.incidents, options);

    return new Promise((resolve, reject) => {
      const {baseQuery = null, filters = []} = options;
      const likes = getWhereFromFilters(filters);
      const where = joinWhereClause([baseQuery, likes]);

      // Apply filter to FeatureLayer
      layer.definitionExpression = options.isFilterAppliedToMap
          ? where
          : baseQuery;
    });
  }

  // Execute a QueryTask against a layer by url. Second argument is a
  // JSAPI Query-like object.
  // NOTE: This is necessary because JSAPI 4.x does not support tables
  // as Feature Layers.
  // eslint-disable-next-line class-methods-use-this
  _executeQueryTask(url, options) {
    return new Promise((resolve, reject) => {
      loadModules(['esri/tasks/QueryTask', 'esri/tasks/support/Query']).then(
          ([QueryTask, Query]) => {
            const query = new Query();

            query.sqlFormat = 'standard';

            const {where, outFields, returnGeometry} = options;

            query.outFields = outFields || ['*'];
            query.where = where;
            query.returnGeometry = returnGeometry || false;

            const qt = new QueryTask(url);
            qt.execute(query).then(resolve, reject);
          }
      );
    });
  }

  // This is a generic function to:
  //  1. aggregate a large number of queries for resource tables where the where clause needs to be split up to
  //  prevent timeouts.
  _queryForRequestFeaturesAndCount(
      layer,
      id,
      options,
      uniqueSet,
      resultFeatures = [],
      resultFields = []
  ) {
    // set a default clientMaxRecordCount in case
    // it was not passed in the options arg or provided in a config file
    options.clientMaxRecordCount = options.clientMaxRecordCount || 10000;
    const whereClauses = [];
    const uniqueSet2 = [...new Set(uniqueSet)]
    for (let i = 0; i < uniqueSet2.length; i += 300) {
      const slicedArray = uniqueSet2.slice(i, i + 300);
      // change where clause to use the queryForFeatures method
      if (id === 'IrwinRID') {
        whereClauses.push(
            {
              baseQuery: (`${id} = '${slicedArray.join(`' OR ${id} = '`)}'`),
              outFields: options.outFields
            });
      } else {
        whereClauses.push(
            {
              where: (`${id} = '${slicedArray.join(`' OR ${id} = '`)}'`),
              outFields: options.outFields
            });
      }
    }

    if (resultFeatures.length >= uniqueSet2.length) {
      // exit recursion and provide the resultFeatures array
      return Promise.resolve(resultFeatures, resultFields);
    }

    // run the queries
    if (id === 'IrwinRID') {
      return Promise.all(whereClauses.map(l => this._queryForFeatures(this.resources, l))).then(responses => {
        // // append features from each query result to the resultFeatures array
        responses.forEach(response => {
          resultFeatures = resultFeatures.concat(response.featureSet.features);
          resultFields = response.featureSet.fields;
        });
        return Promise.resolve({
          features: resultFeatures,
          fields: resultFields
        });
      });
    } else {
      return Promise.all(whereClauses.map(l => this._executeQueryTask(layer, l))).then(responses => {
        // // append features from each query result to the resultFeatures array
        responses.forEach(response => {
          resultFeatures = resultFeatures.concat(response.features);
          resultFields = response.fields;
        });
        return Promise.resolve({
          features: resultFeatures,
          fields: resultFields
        });
      });
    }
  }

  // _fetchLayerInfo(url) {
  //   return new Promise(function(resolve, reject) {
  //     loadModules(['esri/request']).then(
  //       ([request]) => {
  //
  //         request(`${url}?f=json`).then(
  //           response => resolve(response.data),
  //           error => reject(error)
  //         );
  //       }
  //     )
  //   });
  // }
}



// WEBPACK FOOTER //
// ./src/esri/LayerManager.js